import { useCallback, useEffect, useRef } from 'react';
import {
  RegisterClickRequest,
  RegisterQueryRequest,
} from '@weave/schema-gen-ts/dist/schemas/search/history/v1/search.pb';
import { Entity } from '@weave/schema-gen-ts/dist/shared/wsearch/v1/entity.pb';
import { UiSource } from '@weave/schema-gen-ts/dist/shared/wsearch/v1/ui_source.pb';
import { QueryKey, useQueryClient } from 'react-query';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { SearchHistoryApi } from '@frontend/api-search-history';
import { getUser } from '@frontend/auth-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { formatUUID, genUUIDV4 } from '@frontend/string';

const UI_SOURCE = UiSource.CALL_TRANSCRIPT_SEARCH;

const searchHistoryQueryKeys = {
  base: 'callIntelTranscriptSearchHistory',
  registerQuery: (searchId: string) => [searchHistoryQueryKeys.base, 'registerQuery', searchId],
  registerClick: (clickId: string) => [searchHistoryQueryKeys.base, 'registerClick', clickId],
};

type RegisterStatus = 'pending' | 'registering' | 'registered';

type TranscriptSearchSession = {
  /**
   * Search History query id.
   *
   * This id is sent to registerQuery and later reused as registerClick.searchId.
   */
  searchId: string;
  query: string;
  startTime: number;
  callsQueryKeyHash?: string;
  /**
   * True only when the calls API actually entered isFetching=true for this session.
   *
   * This is how we distinguish:
   * - fresh API response: isFetching true -> false
   * - cached response: isFetching never becomes true
   */
  hasCallsFetchStarted: boolean;
  registerStatus: RegisterStatus;
};

type RegisteredTranscriptSearchSession = {
  searchId: string;
  query: string;
  callsQueryKeyHash: string;
};

type RegisterTranscriptSearchQueryOptions = {
  /**
   * Used for cached-result fallback.
   *
   * Fresh searches calculate elapsed from startTime.
   * Cached fallback uses elapsed: 0 because the result is already available.
   */
  elapsedOverride?: number;

  /**
   * Fresh search should require calls API refetch.
   * Cached fallback should not require calls API refetch.
   */
  requireCallsRefetch?: boolean;
};

type UseCallIntelTranscriptSearchHistoryProps = {
  calls: CallIntelTypes.CallRecord[];
  callsQueryKey: QueryKey;
  isFetching: boolean;
  isSmallMode: boolean;
  isTranscriptSearchEnabled: boolean;
  locationIds: string[];
  minSearchLength: number;
  searchQuery: CallIntelTypes.SearchState;
  tableState: {
    pageConfig: {
      pageNumber: number;
      pageSize: number;
    };
  };
};

const getQueryKeyHash = (queryKey: QueryKey) => JSON.stringify(queryKey);

/**
 * Tracks Search History events for transcript/content search in Call Intelligence.
 *
 * registerQuery rules:
 * - Fresh API result:
 *   - wait for calls API to fetch and return.
 *   - then call registerQuery with actual elapsed time.
 *
 * - Cached API result:
 *   - calls API does not enter isFetching=true.
 *   - do not auto-call registerQuery in the effect.
 *   - if user clicks and we already have a searchId in memory, use it.
 *   - if user clicks and no searchId exists, call registerQuery with elapsed: 0,
 *     wait for success, then register the same click.
 *
 * registerClick rules:
 * - Never call registerClick before registerQuery succeeds.
 * - Fresh in-flight registerQuery clicks are ignored.
 * - Cached missing-searchId clicks can wait for elapsed: 0 registerQuery and then register the same click.
 */
export const useCallIntelTranscriptSearchHistory = ({
  calls,
  callsQueryKey,
  isFetching,
  isSmallMode,
  isTranscriptSearchEnabled,
  locationIds,
  minSearchLength,
  searchQuery,
  tableState,
}: UseCallIntelTranscriptSearchHistoryProps) => {
  const queryClient = useQueryClient();
  const { selectedOrgId } = useAppScopeStore();

  const user = getUser();
  const userId = user?.userID;

  const searchSessionRef = useRef<TranscriptSearchSession | null>(null);

  const registeredSearchSessionByQueryKeyRef = useRef<Map<string, RegisteredTranscriptSearchSession>>(new Map());

  const getCurrentCallsQueryKeyHash = useCallback(() => {
    return getQueryKeyHash(callsQueryKey);
  }, [callsQueryKey]);

  const isValidTranscriptContentSearch = useCallback(
    (query: string, searchType?: CallIntelTypes.SearchTypeEnum) => {
      return (
        isTranscriptSearchEnabled &&
        query.trim().length >= minSearchLength &&
        searchType === CallIntelTypes.SearchTypeEnum.SEARCH_BY_CONTENT
      );
    },
    [isTranscriptSearchEnabled, minSearchLength]
  );

  const resetTranscriptSearchHistory = useCallback(() => {
    searchSessionRef.current = null;
  }, []);

  const isCurrentSessionValid = useCallback(
    (session: TranscriptSearchSession) => {
      return (
        session.query === searchQuery.query && isValidTranscriptContentSearch(searchQuery.query, searchQuery.searchType)
      );
    },
    [isValidTranscriptContentSearch, searchQuery.query, searchQuery.searchType]
  );

  /**
   * Starts a new transcript-search tracking session.
   * This should be called when the debounced transcript search is committed.
   */
  const startTranscriptSearchHistory = useCallback(
    (query: string, searchType?: CallIntelTypes.SearchTypeEnum) => {
      if (!isValidTranscriptContentSearch(query, searchType)) {
        resetTranscriptSearchHistory();
        return;
      }

      searchSessionRef.current = {
        searchId: genUUIDV4(),
        query,
        startTime: Date.now(),
        hasCallsFetchStarted: false,
        registerStatus: 'pending',
      };
    },
    [isValidTranscriptContentSearch, resetTranscriptSearchHistory]
  );

  const getResultCallIds = useCallback(() => {
    return calls.flatMap((call) => {
      const formattedCallId = call.id ? formatUUID(call.id) : undefined;
      return formattedCallId ? [formattedCallId] : [];
    });
  }, [calls]);

  /**
   * Registers the current transcript search query.
   *
   * Fresh result:
   * - requireCallsRefetch = true
   * - session.hasCallsFetchStarted must be true
   * - elapsed is calculated from session.startTime
   *
   * Cached missing-searchId fallback:
   * - requireCallsRefetch = false
   * - elapsedOverride = 0
   * - the caller may await this and then registerClick for the same click
   */
  const registerTranscriptSearchQuery = useCallback(
    async ({ elapsedOverride, requireCallsRefetch = true }: RegisterTranscriptSearchQueryOptions = {}): Promise<
      string | undefined
    > => {
      const session = searchSessionRef.current;

      if (!session) return;
      if (!isCurrentSessionValid(session)) return;
      if (isFetching) return;

      if (requireCallsRefetch && !session.hasCallsFetchStarted) return;

      if (session.registerStatus === 'registered') {
        return session.searchId;
      }

      /**
       * For fresh in-flight registration, do not wait and do not queue clicks.
       * Clicks before registerQuery success are ignored.
       */
      if (session.registerStatus === 'registering') {
        return;
      }

      const callsQueryKeyHash = session.callsQueryKeyHash ?? getCurrentCallsQueryKeyHash();

      session.callsQueryKeyHash = callsQueryKeyHash;
      session.registerStatus = 'registering';

      const request: RegisterQueryRequest = {
        id: session.searchId,
        orgId: selectedOrgId,
        locationIds,
        userId,
        uiSource: UI_SOURCE,
        elapsed: elapsedOverride ?? Date.now() - session.startTime,
        userAgent: navigator.userAgent,
        query: session.query,
        results: [
          {
            entity: Entity.ENTITY_CALL_TRANSCRIPT,
            primaryKeys: getResultCallIds(),
          },
        ],
      };

      try {
        await queryClient.fetchQuery({
          queryKey: searchHistoryQueryKeys.registerQuery(session.searchId),
          queryFn: () => SearchHistoryApi.registerQuery(request),
        });

        session.registerStatus = 'registered';

        registeredSearchSessionByQueryKeyRef.current.set(callsQueryKeyHash, {
          searchId: session.searchId,
          query: session.query,
          callsQueryKeyHash,
        });

        return session.searchId;
      } catch (error) {
        session.registerStatus = 'pending';
        console.error('Failed to register transcript search query', error);
        return undefined;
      }
    },
    [
      getCurrentCallsQueryKeyHash,
      getResultCallIds,
      isCurrentSessionValid,
      isFetching,
      locationIds,
      queryClient,
      selectedOrgId,
      userId,
    ]
  );

  const getClickedItemPosition = useCallback(
    (callId?: string) => {
      if (!callId) return undefined;

      const clickedIndex = calls.findIndex((call) => call.id === callId);

      if (clickedIndex < 0) return undefined;

      if (isSmallMode) {
        return clickedIndex;
      }

      return (tableState.pageConfig.pageNumber - 1) * tableState.pageConfig.pageSize + clickedIndex;
    },
    [calls, isSmallMode, tableState.pageConfig.pageNumber, tableState.pageConfig.pageSize]
  );

  /**
   * Returns a searchId only when registerQuery has already succeeded.
   */
  const getRegisteredSearchIdForClick = useCallback(() => {
    const currentCallsQueryKeyHash = getCurrentCallsQueryKeyHash();
    const activeSession = searchSessionRef.current;

    if (activeSession && isCurrentSessionValid(activeSession)) {
      if (activeSession.hasCallsFetchStarted) {
        if (
          activeSession.registerStatus === 'registered' &&
          activeSession.callsQueryKeyHash === currentCallsQueryKeyHash
        ) {
          return activeSession.searchId;
        }

        return undefined;
      }

      /**
       * Cached fallback path
       */
      if (
        activeSession.registerStatus === 'registered' &&
        activeSession.callsQueryKeyHash === currentCallsQueryKeyHash
      ) {
        return activeSession.searchId;
      }
    }

    /**
     * Cached repeat search while component is still mounted.
     */
    return registeredSearchSessionByQueryKeyRef.current.get(currentCallsQueryKeyHash)?.searchId;
  }, [getCurrentCallsQueryKeyHash, isCurrentSessionValid]);

  const registerCachedSearchForCurrentClick = useCallback(async () => {
    const session = searchSessionRef.current;

    if (!session) return;
    if (!isCurrentSessionValid(session)) return;
    if (isFetching) return;

    if (session.hasCallsFetchStarted) return;

    if (session.registerStatus !== 'pending') return;

    return registerTranscriptSearchQuery({
      elapsedOverride: 0,
      requireCallsRefetch: false,
    });
  }, [isCurrentSessionValid, isFetching, registerTranscriptSearchQuery]);

  /**
   * Registers a click on a transcript search result.
   *
   * registerClick is sent only after a successful registerQuery.
   *
   * Fresh search:
   * - if registerQuery succeeded, send registerClick.
   * - if registerQuery is pending/registering, ignore click.
   *
   * Cached search:
   * - if searchId exists in memory, send registerClick.
   * - if searchId is missing, call registerQuery(elapsed: 0), wait for success,
   *   then send registerClick for this same click.
   */
  const registerTranscriptSearchClick = useCallback(
    async (call: CallIntelTypes.CallRecord) => {
      try {
        if (isFetching) return;

        if (!isValidTranscriptContentSearch(searchQuery.query, searchQuery.searchType)) {
          return;
        }

        let searchId = getRegisteredSearchIdForClick();

        if (!searchId) {
          searchId = await registerCachedSearchForCurrentClick();
        }

        if (!searchId) return;

        const formattedCallId = call.id ? formatUUID(call.id) : undefined;
        if (!formattedCallId) return;

        const itemPosition = getClickedItemPosition(call.id);
        if (itemPosition === undefined) return;

        const clickId = genUUIDV4();

        const request: RegisterClickRequest = {
          id: clickId,
          orgId: selectedOrgId,
          locationIds,
          userId,
          userAgent: navigator.userAgent,
          uiSource: UI_SOURCE,
          entity: Entity.ENTITY_CALL_TRANSCRIPT,
          entityId: formattedCallId,
          itemId: formattedCallId,
          entityPosition: 0,
          itemPosition,
          searchId,
        };

        await queryClient.fetchQuery({
          queryKey: searchHistoryQueryKeys.registerClick(clickId),
          queryFn: () => SearchHistoryApi.registerClick(request),
        });
      } catch (error) {
        console.error('Failed to register transcript search click', error);
      }
    },
    [
      getClickedItemPosition,
      getRegisteredSearchIdForClick,
      isFetching,
      isValidTranscriptContentSearch,
      locationIds,
      queryClient,
      registerCachedSearchForCurrentClick,
      searchQuery.query,
      searchQuery.searchType,
      selectedOrgId,
      userId,
    ]
  );

  /**
   * Watches the calls API lifecycle for fresh searches.
   *
   * Fresh search flow:
   * 1. startTranscriptSearchHistory creates a pending session.
   * 2. calls API enters isFetching=true.
   * 3. mark hasCallsFetchStarted=true and capture callsQueryKeyHash.
   * 4. calls API returns, isFetching=false.
   * 5. call registerQuery once with actual elapsed time.
   *
   * Cached search flow:
   * 1. startTranscriptSearchHistory creates a pending session.
   * 2. calls API does not enter isFetching=true.
   * 3. this effect does not call registerQuery.
   * 4. click path handles it:
   *    - use existing searchId if available.
   *    - otherwise call registerQuery(elapsed: 0), then registerClick.
   */
  useEffect(() => {
    const session = searchSessionRef.current;

    if (!session) return;
    if (!isCurrentSessionValid(session)) return;

    if (isFetching) {
      session.hasCallsFetchStarted = true;
      session.callsQueryKeyHash = getCurrentCallsQueryKeyHash();
      return;
    }

    if (!session.hasCallsFetchStarted) return;
    if (session.registerStatus !== 'pending') return;

    registerTranscriptSearchQuery({
      requireCallsRefetch: true,
    });
  }, [getCurrentCallsQueryKeyHash, isCurrentSessionValid, isFetching, registerTranscriptSearchQuery]);

  /**
   * Clears only the active tracking session when search becomes invalid.
   *
   * Do not clear registeredSearchSessionByQueryKeyRef here.
   * Keeping it allows cached repeat searches to reuse a successful searchId while the component remains mounted.
   */
  useEffect(() => {
    if (!isValidTranscriptContentSearch(searchQuery.query, searchQuery.searchType)) {
      resetTranscriptSearchHistory();
    }
  }, [isValidTranscriptContentSearch, resetTranscriptSearchHistory, searchQuery.query, searchQuery.searchType]);

  return {
    registerTranscriptSearchClick,
    resetTranscriptSearchHistory,
    startTranscriptSearchHistory,
  };
};
